import { FC } from "react";
import {
    useMessageRedirect,
    useResolveUploadedPath,
    useTrans,
} from "../../../hooks";
import {
    InternshipSchedule,
    School,
    SchoolBatch,
    Training,
    TrainingYear,
    User,
} from "../../../apis";
import {
    Text,
    Badge,
    HStack,
    Stack,
    VStack,
    Box,
    Divider,
} from "@chakra-ui/react";
import {
    AppRenderDetailChatItem,
    AppRenderDetailItem,
} from "../../../components";
import { APPC, UTIL } from "../../../config";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";

export const InternshipScheduleStudentDetailView: FC<{
    data: User;
    internshipSchedule: InternshipSchedule | null;
    internshipCoordinatorTrainer: User[];
    internshipReferenceTrainer?: User;
}> = ({
    data,
    internshipSchedule,
    internshipCoordinatorTrainer,
    internshipReferenceTrainer,
}) => {
    const { t } = useTrans();
    const dayjs = UTIL.getDayJs();
    const { redirectMessageO2O, redirectMessageInternship } =
        useMessageRedirect();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);
    const isCompany = (activeSchoolOrCompany?.["@id"] as string).startsWith(
        "/api/companies/"
    );

    return (
        <VStack gap={8} alignItems={"stretch"}>
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <Box w={{ base: "full", sm: "full", md: "50%" }}>
                    <AppRenderDetailItem
                        renderType="IMAGE"
                        imageUrl={useResolveUploadedPath(
                            APPC.BE.Upload.FILETYPE.FILETYPE_USER_AVATAR,
                            data?.imageName,
                            undefined
                        )}
                        isAvatar={true}
                    />
                </Box>
                <Box w={{ base: "full", sm: "full", md: "50%" }}>
                    <VStack justifyContent={"space-between"} gap={8}>
                        <AppRenderDetailChatItem
                            label={t("ent.User:name.label")}
                            users={data ? [data] : []}
                            onClick={(user) =>
                                redirectMessageInternship(
                                    user,
                                    internshipSchedule as InternshipSchedule,
                                    (user.schools?.[0] as School)?.id
                                )
                            }
                        />
                    </VStack>
                </Box>
            </Stack>
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailItem
                    label={t("ent.User:phone.label")}
                    value={data?.phone}
                />
                {isCompany ? (
                    <AppRenderDetailChatItem
                        label={t("ent.User:studentTrainer.label")}
                        users={
                            data?.studentTrainer
                                ? [data?.studentTrainer as User]
                                : []
                        }
                        onClick={(user) =>
                            redirectMessageO2O(
                                user,
                                undefined,
                                (user.schools?.[0] as School)?.id
                            )
                        }
                    />
                ) : (
                    <AppRenderDetailItem
                        label={t("ent.User:studentTrainer.label")}
                        value={(data?.studentTrainer as User)?.fullName}
                    />
                )}
            </Stack>
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailItem
                    label={t("ent.User:training.label")}
                    value={(data?.training as Training)?.name}
                />
                <AppRenderDetailItem
                    label={t("ent.User:trainingYear.label")}
                    value={`${(data?.trainingYear as TrainingYear)?.code} - ${(data?.trainingYear as TrainingYear)?.year}`}
                />
            </Stack>
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailItem
                    label={t("ent.User:dob.label")}
                    value={
                        data.dob
                            ? dayjs(data?.dob).format(APPC.APP_DATE_FORMAT)
                            : ""
                    }
                />
                <AppRenderDetailItem
                    label={t("ent.User:nationality.label")}
                    value={data?.nationality}
                />
            </Stack>
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailItem
                    label={t("ent.User:schoolBatch.label")}
                    value={(data?.schoolBatch as SchoolBatch)?.name}
                />
                <AppRenderDetailItem
                    label={t("ent.User:status.label")}
                    value={data?.status}
                />
            </Stack>
            <Stack
                w={"full"}
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <Box w={{ base: "full", sm: "full", md: "50%" }}>
                    <AppRenderDetailItem
                        label={t("ent.User:address.label")}
                        value={data?.address}
                        noOfLines={5}
                    />
                </Box>
                <Box w={{ base: "full", sm: "full", md: "50%" }}>
                    <VStack justifyContent={"space-between"} gap={8}>
                        <AppRenderDetailItem
                            label={t("ent.User:zipCode.label")}
                            value={data.zipCode}
                        />
                        <AppRenderDetailItem
                            label={t("ent.User:city.label")}
                            value={data.city}
                        />
                    </VStack>
                </Box>
            </Stack>
            <AppRenderDetailItem label={t("ent.User:isActive.label")}>
                <HStack>
                    <Badge variant={data?.isActive ? "green" : "red"}>
                        {t(
                            `cmn:label.active.${data?.isActive ? data?.isActive : "null"}`
                        )}
                    </Badge>
                </HStack>
            </AppRenderDetailItem>
            <Box>
                <Text textStyle={"mediumMd"}>
                    {t("padm.InternshipScheduleStudentPage:text.schoolInfo")}
                </Text>
                <Divider borderColor={"var(--chakra-colors-greyT65)"} mt={2} />
            </Box>
            <AppRenderDetailItem
                label={t("ent.School:name.label")}
                value={(data?.schools as School[])[0]?.name}
            />
            <Stack
                gap={8}
                direction={{
                    base: "column",
                    sm: "column",
                    md: "row",
                }}
            >
                <AppRenderDetailChatItem
                    label={t(
                        "ent.Internship:internshipCoordinatorTrainer.label"
                    )}
                    users={internshipCoordinatorTrainer || []}
                    onClick={(user) =>
                        redirectMessageO2O(
                            user,
                            undefined,
                            (user.schools?.[0] as School)?.id
                        )
                    }
                />
                <AppRenderDetailChatItem
                    label={t("ent.Internship:internshipReferenceTrainer.label")}
                    users={
                        internshipReferenceTrainer
                            ? [internshipReferenceTrainer]
                            : []
                    }
                    onClick={(user) =>
                        redirectMessageO2O(
                            user,
                            undefined,
                            (user.schools?.[0] as School)?.id
                        )
                    }
                />
            </Stack>
        </VStack>
    );
};
